import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { apiClient } from '../../utils/api';
import '../common/Modal.css';

export function ReportIssueModal({
    isOpen,
    onClose,
    reasons,
    loadingReasons,
    reasonsError,
    onRefreshReasons,
    devices,
    loadingDevices,
    devicesError,
    onRefreshDevices,
    onSubmit,
}) {
    const [formState, setFormState] = useState({
        deviceId: '',
        problemAreaId: '',
        problemServiceId: '',
        reportReason: '',
        description: '',
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [problemAreas, setProblemAreas] = useState([]);
    const [problemAreasLoading, setProblemAreasLoading] = useState(false);
    const [problemAreasError, setProblemAreasError] = useState(null);

    const [problemServices, setProblemServices] = useState([]);
    const [problemServicesLoading, setProblemServicesLoading] = useState(false);
    const [problemServicesError, setProblemServicesError] = useState(null);

    const reasonOptions = useMemo(
        () => reasons.map((reason) => ({ value: String(reason.id), label: reason.description })),
        [reasons]
    );

    const deviceOptions = useMemo(
        () =>
            devices.map((device) => {
                const label = device.device_name
                    ? `${device.device_name}${device.mac ? ` (${device.mac})` : ''}`
                    : device.mac || `Device ${device.id}`;
                return { value: String(device.id), label };
            }),
        [devices]
    );

    const problemAreaOptions = useMemo(
        () => problemAreas.map((area) => ({ value: String(area.id), label: area.type_description })),
        [problemAreas]
    );

    const problemServiceOptions = useMemo(
        () => problemServices.map((service) => ({ value: String(service.id), label: service.detail })),
        [problemServices]
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setFormState({ deviceId: '', problemAreaId: '', problemServiceId: '', reportReason: '', description: '' });
        setFieldErrors({});
        setSubmitError(null);
        setSubmitting(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;
        setProblemAreasLoading(true);
        setProblemAreasError(null);
        apiClient
            .getUserProblemAreas()
            .then((data) => {
                if (cancelled) {
                    return;
                }
                setProblemAreas(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                setProblemAreas([]);
                setProblemAreasError(err?.message || 'Failed to load problem areas.');
            })
            .finally(() => {
                if (!cancelled) {
                    setProblemAreasLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!formState.problemAreaId) {
            setProblemServices([]);
            setProblemServicesError(null);
            setProblemServicesLoading(false);
            return;
        }

        let cancelled = false;
        setProblemServicesLoading(true);
        setProblemServicesError(null);
        apiClient
            .getUserProblemServices(formState.problemAreaId)
            .then((data) => {
                if (cancelled) {
                    return;
                }
                setProblemServices(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                setProblemServices([]);
                setProblemServicesError(err?.message || 'Failed to load problem services.');
            })
            .finally(() => {
                if (!cancelled) {
                    setProblemServicesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [formState.problemAreaId, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (!formState.deviceId && deviceOptions.length === 1) {
            setFormState((prev) => ({ ...prev, deviceId: deviceOptions[0].value }));
        }
    }, [deviceOptions, formState.deviceId, isOpen]);

    if (!isOpen) {
        return null;
    }

    const validate = () => {
        const errors = {};
        if (!formState.deviceId) {
            errors.deviceId = 'Select a device';
        }
        if (!formState.reportReason) {
            errors.reportReason = 'Select a report reason';
        }
        if (!formState.description.trim()) {
            errors.description = 'Description is required';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) {
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            await onSubmit({
                device_id: Number(formState.deviceId),
                report_reason: Number(formState.reportReason),
                description: formState.description.trim(),
                problem_area_id: formState.problemAreaId ? Number(formState.problemAreaId) : null,
                problem_service_id: formState.problemServiceId ? Number(formState.problemServiceId) : null,
            });
        } catch (err) {
            setSubmitError(err?.message || 'Failed to submit issue');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget && !submitting) {
            onClose();
        }
    };

    const disableSubmit =
        submitting || loadingReasons || loadingDevices || !reasonOptions.length || !deviceOptions.length;

    return (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
            <div className="modal" onClick={(event) => event.stopPropagation()}>
                <div className="modal__header">
                    <h3>Report an issue</h3>
                    <button type="button" className="modal__close" onClick={onClose} disabled={submitting} aria-label="Close report issue modal">
                        ×
                    </button>
                </div>
                <form className="modal__form" onSubmit={handleSubmit}>
                    <div className="form-field">
                        <label htmlFor="report-issue-device" className="form-label">
                            Device
                        </label>
                        <div className="form-input-group">
                            <select
                                id="report-issue-device"
                                value={formState.deviceId}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setFormState((prev) => ({ ...prev, deviceId: value }));
                                    if (fieldErrors.deviceId) {
                                        setFieldErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.deviceId;
                                            return next;
                                        });
                                    }
                                }}
                                aria-invalid={Boolean(fieldErrors.deviceId)}
                                disabled={loadingDevices || submitting}
                            >
                                <option value="">Select a device</option>
                                {deviceOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {devicesLoadingState({
                                loadingDevices,
                                devicesError,
                                onRefreshDevices,
                            })}
                            {fieldErrors.deviceId ? (
                                <p className="form-error" role="alert">
                                    {fieldErrors.deviceId}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="report-issue-problem-area" className="form-label">
                            Problem area
                        </label>
                        <div className="form-input-group">
                            <select
                                id="report-issue-problem-area"
                                value={formState.problemAreaId}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setFormState((prev) => ({
                                        ...prev,
                                        problemAreaId: value,
                                        problemServiceId: '',
                                    }));
                                }}
                                disabled={problemAreasLoading || submitting}
                            >
                                <option value="">Select a problem area</option>
                                {problemAreaOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {problemAreasLoadingState({
                                loading: problemAreasLoading,
                                error: problemAreasError,
                                onRetry: () => {
                                    setProblemAreasLoading(true);
                                    setProblemAreasError(null);
                                    apiClient
                                        .getUserProblemAreas()
                                        .then((data) => setProblemAreas(Array.isArray(data) ? data : []))
                                        .catch((err) => {
                                            setProblemAreas([]);
                                            setProblemAreasError(err?.message || 'Failed to load problem areas.');
                                        })
                                        .finally(() => setProblemAreasLoading(false));
                                },
                            })}
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="report-issue-problem-service" className="form-label">
                            Service
                        </label>
                        <div className="form-input-group">
                            <select
                                id="report-issue-problem-service"
                                value={formState.problemServiceId}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setFormState((prev) => ({ ...prev, problemServiceId: value }));
                                }}
                                disabled={!formState.problemAreaId || problemServicesLoading || submitting}
                            >
                                <option value="">Select a service</option>
                                {problemServiceOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {problemServicesLoadingState({
                                loading: problemServicesLoading,
                                error: problemServicesError,
                                onRetry: () => {
                                    if (!formState.problemAreaId) {
                                        return;
                                    }
                                    setProblemServicesLoading(true);
                                    setProblemServicesError(null);
                                    apiClient
                                        .getUserProblemServices(formState.problemAreaId)
                                        .then((data) => setProblemServices(Array.isArray(data) ? data : []))
                                        .catch((err) => {
                                            setProblemServices([]);
                                            setProblemServicesError(err?.message || 'Failed to load problem services.');
                                        })
                                        .finally(() => setProblemServicesLoading(false));
                                },
                            })}
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="report-issue-reason" className="form-label">
                            Reason
                        </label>
                        <div className="form-input-group">
                            <select
                                id="report-issue-reason"
                                value={formState.reportReason}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setFormState((prev) => ({ ...prev, reportReason: value }));
                                    if (fieldErrors.reportReason) {
                                        setFieldErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.reportReason;
                                            return next;
                                        });
                                    }
                                }}
                                aria-invalid={Boolean(fieldErrors.reportReason)}
                                disabled={loadingReasons || submitting}
                            >
                                <option value="">Select a reason</option>
                                {reasonOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {reasonsLoadingState({
                                loadingReasons,
                                reasonsError,
                                onRefreshReasons,
                            })}
                            {fieldErrors.reportReason ? (
                                <p className="form-error" role="alert">
                                    {fieldErrors.reportReason}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="report-issue-description" className="form-label">
                            Description
                        </label>
                        <div className="form-input-group">
                            <textarea
                                id="report-issue-description"
                                rows={4}
                                value={formState.description}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setFormState((prev) => ({ ...prev, description: value }));
                                    if (fieldErrors.description) {
                                        setFieldErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.description;
                                            return next;
                                        });
                                    }
                                }}
                                aria-invalid={Boolean(fieldErrors.description)}
                                disabled={submitting}
                                placeholder="Provide details so we can help troubleshoot"
                            />
                            {fieldErrors.description ? (
                                <p className="form-error" role="alert">
                                    {fieldErrors.description}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    {submitError ? (
                        <div className="form-error" role="alert" style={{ marginBottom: '1rem' }}>
                            {submitError}
                        </div>
                    ) : null}

                    <div className="modal__footer">
                        <button type="button" className="secondary-btn" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="primary-btn" disabled={disableSubmit}>
                            {submitting ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

ReportIssueModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    reasons: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            description: PropTypes.string.isRequired,
        })
    ).isRequired,
    loadingReasons: PropTypes.bool,
    reasonsError: PropTypes.string,
    onRefreshReasons: PropTypes.func,
    devices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            device_name: PropTypes.string,
            mac: PropTypes.string,
        })
    ).isRequired,
    loadingDevices: PropTypes.bool,
    devicesError: PropTypes.string,
    onRefreshDevices: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
};

ReportIssueModal.defaultProps = {
    loadingReasons: false,
    reasonsError: null,
    onRefreshReasons: undefined,
    loadingDevices: false,
    devicesError: null,
    onRefreshDevices: undefined,
};

function devicesLoadingState({ loadingDevices, devicesError, onRefreshDevices }) {
    if (loadingDevices) {
        return <p className="form-helper-text">Loading devices…</p>;
    }
    if (devicesError) {
        return (
            <div className="form-helper-text" role="alert">
                {devicesError}
                {typeof onRefreshDevices === 'function' ? (
                    <button
                        type="button"
                        className="secondary-btn"
                        style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem' }}
                        onClick={onRefreshDevices}
                    >
                        Retry
                    </button>
                ) : null}
            </div>
        );
    }
    return null;
}

function reasonsLoadingState({ loadingReasons, reasonsError, onRefreshReasons }) {
    if (loadingReasons) {
        return <p className="form-helper-text">Loading report reasons…</p>;
    }
    if (reasonsError) {
        return (
            <div className="form-helper-text" role="alert">
                {reasonsError}
                {typeof onRefreshReasons === 'function' ? (
                    <button
                        type="button"
                        className="secondary-btn"
                        style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem' }}
                        onClick={onRefreshReasons}
                    >
                        Retry
                    </button>
                ) : null}
            </div>
        );
    }
    return null;
}

function problemAreasLoadingState({ loading, error, onRetry }) {
    if (loading) {
        return <p className="form-helper-text">Loading problem areas…</p>;
    }
    if (error) {
        return (
            <div className="form-helper-text" role="alert">
                {error}
                {typeof onRetry === 'function' ? (
                    <button
                        type="button"
                        className="secondary-btn"
                        style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem' }}
                        onClick={onRetry}
                    >
                        Retry
                    </button>
                ) : null}
            </div>
        );
    }
    return null;
}

function problemServicesLoadingState({ loading, error, onRetry }) {
    if (loading) {
        return <p className="form-helper-text">Loading services…</p>;
    }
    if (error) {
        return (
            <div className="form-helper-text" role="alert">
                {error}
                {typeof onRetry === 'function' ? (
                    <button
                        type="button"
                        className="secondary-btn"
                        style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem' }}
                        onClick={onRetry}
                    >
                        Retry
                    </button>
                ) : null}
            </div>
        );
    }
    return null;
}
